var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const debug = false;
const channelBlockTypes = [
    'automixer',
    'effectReturn',
    'input',
    'output',
    'preset',
    'zone',
];
const matrixBlockTypes = ['crossPoint', 'group', 'inputPatch', 'router'];
export const modelNames = ['mtx3', 'mtx5d'];
function isChannelBlockInfo(blockInfo) {
    return channelBlockTypes.includes(blockInfo.blockType);
}
function isMatrixBlockInfo(blockInfo) {
    return matrixBlockTypes.includes(blockInfo.blockType);
}
function isModelName(modelName) {
    return modelNames.includes(modelName);
}
function isYamahaMtxAudioChannelConfig(config) {
    return (typeof config === 'object' &&
        config !== null &&
        'channel' in config &&
        typeof config.channel === 'string' &&
        (!('index1' in config) || ('index1' in config && typeof config.index1 === 'number')) &&
        (!('index2' in config) || ('index2' in config && typeof config.index2 === 'number')));
}
function isYamahaMtxPresetMuteConfig(config) {
    return (typeof config === 'object' &&
        config !== null &&
        'mutePreset' in config &&
        isYamahaMtxAudioChannelConfig(config.mutePreset) &&
        'muteStatus' in config &&
        isYamahaMtxAudioChannelConfig(config.muteStatus) &&
        'unmutePreset' in config &&
        isYamahaMtxAudioChannelConfig(config.unmutePreset));
}
function makeLabels(spec) {
    const labels = [];
    for (const item of spec) {
        if (typeof item === 'string') {
            labels.push(item);
        }
        else {
            const [prefix, start, end, fmt] = item;
            for (let i = start; i <= end; i++) {
                if (fmt === 'stereo') {
                    labels.push(`${prefix}${i}L`);
                    labels.push(`${prefix}${i}R`);
                }
                else {
                    labels.push(`${prefix}${i}`);
                }
            }
        }
    }
    return labels;
}
const yamahaMtxModelNumbers = {
    mtx3: '-OQ9zFMwEf1_JaA7bCOJ',
    mtx5d: '-OQ9zIuDmJv-h8HOdrY5',
};
const productInfo = {
    mtx3: [
        {
            blockType: 'automixer',
            indexed: true,
            labels: ['Group a', 'Group b'],
            mute: {
                uniqueId: 50011,
                paramNo: 1,
                invertMutePolarity: true,
                // Automixer channels enumerated with yPos (not xPos)
                useIndex2ForXPos: true,
            },
            name: 'Automixer',
        },
        {
            blockType: 'crossPoint',
            labels: {
                input: makeLabels([
                    ['CH ', 1, 8],
                    ['STIN ', 1, 3, 'stereo'],
                    ['CH ', 9, 16],
                    ['FxRTN ', 1, 2, 'stereo'],
                ]),
                output: makeLabels([['Zone ', 1, 8]]),
            },
            mute: { uniqueId: 30002, elmNo: 1, paramNo: 1 },
            name: 'Matrix',
            volume: { uniqueId: 30002, elmNo: 1, paramNo: 0 },
            volumeRanges: {
                trim: { max: 0, min: -2400, round: 1 },
                volume: { max: 0, min: -5000, round: 1 },
            },
        },
        {
            blockType: 'effectReturn',
            indexed: true,
            labels: makeLabels([['FxRTN ', 1, 2, 'stereo']]),
            mute: { uniqueId: 20008, elmNo: 1 },
            name: 'Effect Return',
            volume: { uniqueId: 20008, elmNo: 0 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        // No paging groups in the MTX3
        {
            blockType: 'input',
            indexed: true,
            labels: makeLabels([
                ['CH ', 1, 8],
                ['STIN ', 1, 3, 'stereo'],
                ['CH ', 9, 16],
                ['FxRTN ', 1, 2, 'stereo'],
            ]),
            mute: { uniqueId: 60001 },
            name: 'Input',
            volume: { uniqueId: 60000 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        {
            blockType: 'inputPatch',
            labels: {
                input: makeLabels([
                    ['IN ', 1, 8],
                    ['ST IN ', 1, 2, 'stereo'],
                    'SD IN L',
                    'SD IN R',
                    ['YDIF ', 1, 16],
                ]),
                output: makeLabels([
                    ['CH ', 1, 8],
                    ['ST IN ', 1, 3, 'stereo'],
                    ['CH ', 8, 16],
                ]),
            },
            mute: [{ uniqueId: 20001, isRouter: true }],
            name: 'Input Patch',
        },
        {
            blockType: 'output',
            indexed: true,
            labels: makeLabels([['OUT ', 1, 8]]),
            mute: { uniqueId: 20017, elmNo: 1 },
            name: 'Output',
            volume: { uniqueId: 20017, elmNo: 0 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        {
            blockType: 'router',
            labels: {
                input: makeLabels([
                    ['Zone ', 1, 8],
                    ['YDIF ', 1, 16],
                ]),
                output: makeLabels([['OUT ', 1, 8]]),
            },
            mute: { uniqueId: 20023, index1: { start: 0, end: 23 }, isRouter: true },
            name: 'Router',
        },
        {
            blockType: 'preset',
            labels: (socket, log) => __awaiter(void 0, void 0, void 0, function* () {
                const labels = [];
                const match = yield socket.send(`ssnum`, /OK\s+ssnum\s+(\d+)/, ['presets']);
                const numPresets = parseInt(match[1]);
                if (debug)
                    log.debug(`numPresets = ${numPresets}`);
                try {
                    for (let i = 0; i < numPresets; i++) {
                        const labelMatch = yield socket.send(`ssinfo ${i}`, /OK\s+ssinfo\s+(\d+)\s+\"[^\"]*\"\s+(\S+)\s+\"([^\"]*)"/i, ['preset']);
                        if (labelMatch && labelMatch[1] === `${i}`) {
                            let lab = labelMatch[3] !== '' ? labelMatch[3] : `Preset ${i}`;
                            if (labelMatch[2] !== 'user')
                                lab += ` (${labelMatch[2]})`;
                            labels.push(lab);
                        }
                    }
                    if (debug)
                        log.debug(`Preset names = ${JSON.stringify(labels)}`);
                }
                catch (error) {
                    if (debug)
                        log.debug(`Error trying to get preset labels`);
                }
                return labels;
            }),
            name: 'Preset',
        },
        {
            blockType: 'zone',
            indexed: true,
            labels: makeLabels([['Zone ', 1, 8]]),
            mute: { uniqueId: 60003 },
            name: 'Zone',
            volume: { uniqueId: 60002 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
    ],
    mtx5d: [
        {
            blockType: 'automixer',
            indexed: true,
            labels: ['Group a', 'Group b', 'Group c', 'Group d'],
            mute: {
                uniqueId: 50011,
                paramNo: 1,
                invertMutePolarity: true,
                // Automixer channels enumerated with yPos (not xPos)
                useIndex2ForXPos: true,
            },
            name: 'Automixer',
        },
        {
            blockType: 'crossPoint',
            labels: {
                input: makeLabels([
                    ['CH ', 1, 16],
                    ['STIN ', 1, 3, 'stereo'],
                    ['CH ', 17, 24],
                    ['FxRTN ', 1, 2, 'stereo'],
                ]),
                output: makeLabels([['Zone ', 1, 16]]),
            },
            mute: { uniqueId: 30002, elmNo: 1, paramNo: 1 },
            name: 'Matrix',
            volume: { uniqueId: 30002, elmNo: 1, paramNo: 0 },
            volumeRanges: {
                trim: { max: 0, min: -2400, round: 1 },
                volume: { max: 0, min: -5000, round: 1 },
            },
        },
        {
            blockType: 'effectReturn',
            indexed: true,
            labels: makeLabels([['FxRTN ', 1, 2, 'stereo']]),
            mute: { uniqueId: 20014, elmNo: 1 },
            name: 'Effect Return',
            volume: { uniqueId: 20014, elmNo: 0 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        {
            blockType: 'group',
            labels: {
                input: makeLabels([['Group ', 1, 24]]),
                output: makeLabels([['Zone ', 1, 16]]),
            },
            mute: { uniqueId: 50012, elmNo: 9, swapPosInNotifications: true },
            name: 'Group',
        },
        {
            blockType: 'input',
            indexed: true,
            labels: makeLabels([
                ['CH ', 1, 16],
                ['STIN ', 1, 3, 'stereo'],
                ['CH ', 17, 24],
                ['FxRTN ', 1, 2, 'stereo'],
            ]),
            mute: { uniqueId: 60001 },
            name: 'Input',
            volume: { uniqueId: 60000 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        {
            blockType: 'inputPatch',
            labels: {
                input: makeLabels([
                    ['IN ', 1, 8],
                    ['ST IN ', 1, 2, 'stereo'],
                    'SD IN L',
                    'SD IN R',
                    ['YDIF ', 1, 16],
                    ['D ', 1, 16],
                    ['SLOT ', 1, 16],
                ]),
                output: makeLabels([
                    ['CH ', 1, 16],
                    ['ST IN ', 1, 3, 'stereo'],
                    ['CH ', 17, 24],
                ]),
            },
            mute: [
                { uniqueId: 20003, index2: { start: 0, end: 7 }, isRouter: true },
                {
                    uniqueId: 20004,
                    index2: { start: 8, end: 61, yPosStart: 0 },
                    isRouter: true,
                },
            ],
            name: 'Input Patch',
        },
        {
            blockType: 'output',
            indexed: true,
            labels: makeLabels([['OUT ', 1, 16]]),
            mute: { uniqueId: 20024, elmNo: 1 },
            name: 'Output',
            volume: { uniqueId: 20024, elmNo: 0 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
        {
            blockType: 'preset',
            labels: (socket, log) => __awaiter(void 0, void 0, void 0, function* () {
                const labels = [];
                const match = yield socket.send(`ssnum`, /OK\s+ssnum\s+(\d+)/, ['presets']);
                const numPresets = parseInt(match[1]);
                if (debug)
                    log.debug(`numPresets = ${numPresets}`);
                try {
                    for (let i = 0; i < numPresets; i++) {
                        const labelMatch = yield socket.send(`ssinfo ${i}`, /OK\s+ssinfo\s+(\d+)\s+\"[^\"]*\"\s+(\S+)\s+\"([^\"]*)"/i, ['preset']);
                        if (labelMatch && labelMatch[1] === `${i}`) {
                            let lab = labelMatch[3] !== '' ? labelMatch[3] : `Preset ${i}`;
                            if (labelMatch[2] !== 'user')
                                lab += ` (${labelMatch[2]})`;
                            labels.push(lab);
                        }
                    }
                    if (debug)
                        log.debug(`Preset names = ${JSON.stringify(labels)}`);
                }
                catch (error) {
                    if (debug)
                        log.debug(`Error trying to get preset labels`);
                }
                return labels;
            }),
            name: 'Preset',
        },
        {
            blockType: 'router',
            labels: {
                input: makeLabels([
                    ['Zone ', 1, 16],
                    ['YDIF ', 1, 16],
                ]),
                output: makeLabels([['OUT ', 1, 16]]),
            },
            mute: { uniqueId: 20023, index1: { start: 0, end: 31 }, isRouter: true },
            name: 'Router',
        },
        {
            blockType: 'zone',
            indexed: true,
            labels: makeLabels([['Zone ', 1, 16]]),
            mute: { uniqueId: 60003 },
            name: 'Zone',
            volume: { uniqueId: 60002 },
            volumeRanges: {
                trim: { max: 1000, min: -1400, round: 1 },
                volume: { max: 1000, min: -4000, round: 1 },
            },
        },
    ],
};
class BlockBase {
    constructor(blockType, name, channels, log, socket, state, mute, volume, volumeRanges) {
        this.blockType = blockType;
        this.name = name;
        this.channels = channels;
        this.log = log;
        this.socket = socket;
        this.state = state;
        this.mute = mute;
        this.volume = volume;
        this.volumeRanges = volumeRanges;
        this._size = 0;
        const type = this.blockType;
        this.filters = {
            amplifier: this.volume !== undefined,
            assistiveListeningMute: this.mute !== undefined,
            assistiveListeningMuteStatus: this.mute !== undefined,
            assistiveListeningVolume: this.volume !== undefined,
            audioInputMute: this.mute !== undefined,
            audioInputMuteStatus: this.mute !== undefined,
            audioInputVolume: this.volume !== undefined,
            mics: this.mute !== undefined,
            micsMuteStatus: this.mute !== undefined,
            overflowRoomMute: this.mute !== undefined,
            overflowRoomMuteStatus: this.mute !== undefined,
            overflowRoomVolume: this.volume !== undefined,
            preset: type === 'preset',
            receiveGain: false,
            soundReinforcementMute: this.mute !== undefined,
            soundReinforcementMuteStatus: this.mute !== undefined,
            soundReinforcementVolume: this.volume !== undefined,
            telephony: false,
        };
    }
    get size() {
        if (Array.isArray(this._size))
            return this._size;
        return [this._size, 0];
    }
    findAllChannelsInColumn(channels, blockType, index2) {
        return channels.filter(channel => {
            const chan = channel instanceof Channel ? channel : channel.muteStatus;
            return chan.block.blockType === blockType && chan.index2 === index2;
        });
    }
    findAllChannelsInColumnExcept(channels, blockType, index1, index2) {
        return channels.filter(channel => {
            const chan = channel instanceof Channel ? channel : channel.muteStatus;
            return (chan.block.blockType === blockType &&
                chan.index2 === index2 &&
                chan.index1 !== index1);
        });
    }
    findCommandForIndices(commands, index1, index2) {
        for (const command of commands) {
            const index1Range = command.index1;
            const index2Range = command.index2;
            if ((index1Range === undefined ||
                (index1 !== undefined &&
                    index1 >= index1Range.start &&
                    index1 <= index1Range.end)) &&
                (index2Range === undefined ||
                    (index2 !== undefined &&
                        index2 >= index2Range.start &&
                        index2 <= index2Range.end))) {
                return command;
            }
        }
        this.log.error(`findCommand: no command found for ${this.blockType} index1: ${index1}, index2: ${index2}`);
        const command = commands[0];
        if (command === undefined) {
            throw new Error(`findCommand: no commands defined for ${this.blockType} index1: ${index1}, index2: ${index2}`);
        }
        return command;
    }
    findCommandForNotification(commands, uniqueId, elmNo, paramNo, indexNo, xPos, yPos) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        for (const command of commands) {
            if (command.swapPosInNotifications || command.useIndex2ForXPos) {
                const tmp = xPos;
                xPos = yPos;
                yPos = tmp;
            }
            if (command.uniqueId !== uniqueId ||
                !(command.elmNo === undefined || command.elmNo === elmNo) ||
                !(command.paramNo === undefined || command.paramNo === paramNo) ||
                !(command.indexNo === undefined || command.indexNo === indexNo)) {
                continue;
            }
            const index1Size = this.size[0];
            const index2Size = this.size[1];
            if (debug) {
                this.log.debug(`findCommandForNotification: xPos: ${xPos}, yPos: ${yPos}, uniqueId: ${uniqueId}, command = ${JSON.stringify(command)}`);
            }
            if (index1Size > 0) {
                if (xPos === undefined)
                    continue;
                const commandXPosStart = (_d = (_b = (_a = command.index1) === null || _a === void 0 ? void 0 : _a.xPosStart) !== null && _b !== void 0 ? _b : (_c = command.index1) === null || _c === void 0 ? void 0 : _c.start) !== null && _d !== void 0 ? _d : 0;
                const commandXPosSize = command.index1 === undefined
                    ? index1Size
                    : command.index1.end - command.index1.start + 1;
                const commandXPosEnd = commandXPosStart + commandXPosSize - 1;
                if (xPos < commandXPosStart || xPos > commandXPosEnd)
                    continue;
            }
            if (index2Size > 0) {
                if (yPos === undefined)
                    continue;
                const commandYPosStart = (_h = (_f = (_e = command.index2) === null || _e === void 0 ? void 0 : _e.yPosStart) !== null && _f !== void 0 ? _f : (_g = command.index2) === null || _g === void 0 ? void 0 : _g.start) !== null && _h !== void 0 ? _h : 0;
                const commandYPosSize = command.index2 === undefined
                    ? index2Size
                    : command.index2.end - command.index2.start + 1;
                const commandYPosEnd = commandYPosStart + commandYPosSize - 1;
                if (yPos < commandYPosStart || yPos > commandYPosEnd)
                    continue;
            }
            if (debug) {
                this.log.debug(`findCommandForNotification: FOUND command = ${JSON.stringify(command)}`);
            }
            return command;
        }
        return undefined;
    }
    getFader(index1, index2, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volume === undefined)
                throw new Error(`fader not supported for ${this.blockType}`);
            const volumes = Array.isArray(this.volume) ? this.volume : [this.volume];
            const command = this.findCommandForIndices(volumes, index1, index2);
            const [cmd, regExp] = this.makeCommandAndRegExp('get', command, index1, index2);
            const match = yield this.socket.send(cmd, new RegExp(`${regExp}\\s+(-?[\\d\\.]+)`, 'i'), tags);
            return parseInt(match[1], 10);
        });
    }
    getMute(index1, index2, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.mute === undefined)
                throw new Error(`mute not supported for ${this.blockType}`);
            const mutes = Array.isArray(this.mute) ? this.mute : [this.mute];
            const command = this.findCommandForIndices(mutes, index1, index2);
            const [cmd, regExp, xPos] = this.makeCommandAndRegExp('get', command, index1, index2);
            const match = yield this.socket.send(cmd, new RegExp(`${regExp}\\s+(-?[\\d\\.]+)|Error\\s+get\\s+InternalError`, 'i'), tags);
            const matchValue = parseInt(match[1], 10);
            // For routers, this crosspoint is muted if the returned value is either 0 (no zone routed
            // to an output) or the zone routed to this output is not this particular output channel.
            // Note the returned match value is the xPos + 1.
            const muteValue = command.isRouter
                ? matchValue === 0 || matchValue !== (xPos !== null && xPos !== void 0 ? xPos : 0) + 1
                : matchValue === 0;
            return command.invertMutePolarity ? !muteValue : muteValue;
        });
    }
    getNotificationMatch(uniqueId, elmNo, paramNo, indexNo, xPos, yPos) {
        if (this.mute !== undefined) {
            const mutes = Array.isArray(this.mute) ? this.mute : [this.mute];
            const command = this.findCommandForNotification(mutes, uniqueId, elmNo, paramNo, indexNo, xPos, yPos);
            if (debug && (command === null || command === void 0 ? void 0 : command.isRouter)) {
                this.log.debug(`baseBlock: getNotificationMatch: router detected, command = ${JSON.stringify(command)}`);
            }
            if (command !== undefined)
                return { matchType: 'mute', command };
        }
        if (this.volume !== undefined) {
            const volumes = Array.isArray(this.volume) ? this.volume : [this.volume];
            const command = this.findCommandForNotification(volumes, uniqueId, elmNo, paramNo, indexNo, xPos, yPos);
            if (command !== undefined)
                return { matchType: 'volume', command };
        }
        return undefined;
    }
    getPos(command, index1, index2) {
        var _a, _b;
        const xPos = index1 !== undefined && ((_a = command.index1) === null || _a === void 0 ? void 0 : _a.xPosStart) !== undefined
            ? index1 - command.index1.start + command.index1.xPosStart
            : index1;
        const yPos = index2 !== undefined && ((_b = command.index2) === null || _b === void 0 ? void 0 : _b.yPosStart) !== undefined
            ? index2 - command.index2.start + command.index2.yPosStart
            : index2;
        return command.useIndex2ForXPos ? [yPos, xPos] : [xPos, yPos];
    }
    handleMuteSideEffects(index1, index2, value) {
        var _a;
        if (value === true)
            return;
        const channels = this.findAllChannelsInColumnExcept(this.channels, this.blockType, index1, index2);
        if (debug) {
            this.log.debug(`handleMuteSideEffects: accessing ${this.blockType}, index1: ${index1}, index2: ${index2}`);
            this.log.debug(`All Configured channels:`);
            for (const chan of this.channels) {
                this.log.debug(`    chan.key: ${chan.key}`);
            }
            this.log.debug(`All filtered channels:`);
            for (const chan of channels) {
                this.log.debug(`    chan.key: ${chan.key}`);
            }
        }
        if (debug) {
            this.log.debug(`handleMuteSideEffects: Muting the channels`);
        }
        for (const chan of channels) {
            if (debug) {
                (_a = this.log) === null || _a === void 0 ? void 0 : _a.debug(`    handleMuteSideEffects: updating mute state of ${chan.key} to true`);
            }
            this.state.mutes.update(chan.key, true);
        }
    }
    loadPreset(index1, index2, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send(`ssrecall ${index1}`, /OK\s+ssrecall\s+(\d+)|ERROR\s+ssrecall/i, tags);
            return match[1] !== undefined;
        });
    }
    makeCommandAndRegExp(cmdType, command, index1, index2) {
        const [xPos, yPos] = this.getPos(command, index1, index2);
        const forcedXPos = command.isRouter ? 0 : xPos;
        const { uniqueId, elmNo, paramNo, indexNo } = command;
        const cmdString = `${cmdType} MTX:mem_512/${uniqueId}/${elmNo !== null && elmNo !== void 0 ? elmNo : 0}/${forcedXPos !== null && forcedXPos !== void 0 ? forcedXPos : 0}` +
            `/${yPos !== null && yPos !== void 0 ? yPos : 0}/${paramNo !== null && paramNo !== void 0 ? paramNo : 0}/${indexNo !== null && indexNo !== void 0 ? indexNo : 0} 0 0`;
        const regExpString = `OK\\s+${cmdType}\\s+MTX:mem_512/${uniqueId}/${elmNo !== null && elmNo !== void 0 ? elmNo : 0}` +
            `/${forcedXPos !== null && forcedXPos !== void 0 ? forcedXPos : 0}/${yPos !== null && yPos !== void 0 ? yPos : 0}/${paramNo !== null && paramNo !== void 0 ? paramNo : 0}/${indexNo !== null && indexNo !== void 0 ? indexNo : 0}\\s+\\d\\s+\\d`;
        if (debug)
            this.log.debug(`makeCommandAndRegExp: cmdString = ${cmdString}`);
        return [cmdString, regExpString, xPos, yPos];
    }
    setFader(index1, index2, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volume === undefined)
                throw new Error(`fader not supported for ${this.blockType}`);
            const volumes = Array.isArray(this.volume) ? this.volume : [this.volume];
            const command = this.findCommandForIndices(volumes, index1, index2);
            const [cmd, regExp] = this.makeCommandAndRegExp('set', command, index1, index2);
            const match = yield this.socket.send(`${cmd} ${value}`, new RegExp(`${regExp}\\s+(-?[\\d\\.]+)`, 'i'), tags);
            return parseInt(match[1], 10);
        });
    }
    setMute(index1, index2, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.mute === undefined)
                throw new Error(`mute not supported for ${this.blockType}`);
            const mutes = Array.isArray(this.mute) ? this.mute : [this.mute];
            const command = this.findCommandForIndices(mutes, index1, index2);
            const [cmd, regExp, xPos] = this.makeCommandAndRegExp('set', command, index1, index2);
            const setValue = command.invertMutePolarity ? !value : value;
            if (command.isRouter) {
                const match = yield this.socket.send(`${cmd} ${setValue ? '0' : (xPos !== null && xPos !== void 0 ? xPos : 0) + 1}`, // add 1 since command uses 1-based index
                new RegExp(`${regExp}\\s+(\\d+)`, 'i'), tags);
                if (debug) {
                    this.log.debug(`setMute: xPos: ${xPos}, index1: ${index1}, index2:${index2}`);
                }
                this.handleMuteSideEffects(index1 !== null && index1 !== void 0 ? index1 : 0, index2 !== null && index2 !== void 0 ? index2 : 0, setValue);
                const matchValue = parseInt(match[1], 10);
                let respValue = matchValue === 0 || matchValue !== (xPos !== null && xPos !== void 0 ? xPos : 0) + 1;
                respValue = command.invertMutePolarity ? !respValue : respValue;
                return respValue;
            }
            const match = yield this.socket.send(`${cmd} ${setValue ? 0 : 1}`, new RegExp(`${regExp}\\s+(-?[\\d\\.]+)|Error\\s+set\\s+InternalError`, 'i'), tags);
            const matchValue = parseInt(match[1], 10);
            let respValue = matchValue === 0;
            respValue = command.invertMutePolarity ? !respValue : respValue;
            return respValue;
        });
    }
    muteColumn(index2) {
        const channels = this.findAllChannelsInColumn(this.channels, this.blockType, index2);
        for (const chan of channels) {
            this.state.mutes.update(chan.key, true);
        }
    }
}
class ChannelBlock extends BlockBase {
    constructor(info, channels, log, socket, state) {
        super(info.blockType, info.name, channels, log, socket, state, info.mute, info.volume, info.volumeRanges);
        this.info = info;
        this.labels = [];
    }
    getDetected() {
        return __awaiter(this, void 0, void 0, function* () {
            this.labels =
                typeof this.info.labels === 'function'
                    ? yield this.info.labels(this.socket, this.log)
                    : this.info.labels;
            this._size = this.labels.length;
            if (this.info.indexed) {
                return [
                    {
                        channel: this.blockType,
                        filters: this.filters,
                        index1Options: this.labels.map((name, index) => ({ index, name })),
                        name: this.name,
                        type: this.blockType,
                    },
                ];
            }
            return this.labels.map((label, index) => ({
                channel: `${this.blockType}.${index}`,
                filters: this.filters,
                name: label,
                type: this.blockType,
            }));
        });
    }
    indicesAreValid(index1, index2) {
        return index1 !== undefined && index1 >= 0 && index1 < this.labels.length;
    }
}
class MatrixBlock extends BlockBase {
    constructor(info, channels, log, socket, state) {
        super(info.blockType, info.name, channels, log, socket, state, info.mute, info.volume, info.volumeRanges);
        this.info = info;
        this.labels = { input: [], output: [] };
    }
    getDetected() {
        return __awaiter(this, void 0, void 0, function* () {
            this.labels =
                typeof this.info.labels === 'function'
                    ? yield this.info.labels(this.socket, this.log)
                    : this.info.labels;
            this._size = [this.labels.input.length, this.labels.output.length];
            return [
                {
                    channel: this.blockType,
                    filters: this.filters,
                    index1Options: this.labels.input.map((name, index) => ({ index, name })),
                    index2Options: this.labels.output.map((name, index) => ({ index, name })),
                    name: this.name,
                    type: this.blockType,
                },
            ];
        });
    }
    indicesAreValid(index1, index2) {
        return (index1 !== undefined &&
            index1 >= 0 &&
            index1 < this.labels.input.length &&
            index2 !== undefined &&
            index2 >= 0 &&
            index2 < this.labels.output.length);
    }
}
class PresetMuteChannel {
    constructor(key, mutePreset, unmutePreset, muteStatus) {
        this.key = key;
        this.mutePreset = mutePreset;
        this.unmutePreset = unmutePreset;
        this.muteStatus = muteStatus;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
    }
    get block() {
        return this.muteStatus.block;
    }
    get index1() {
        return this.muteStatus.index1;
    }
    get index2() {
        return this.muteStatus.index2;
    }
    doesNotificationMatch(uniqueId, elmNo, paramNo, indexNo, xPos, yPos) {
        return this.muteStatus.doesNotificationMatch(uniqueId, elmNo, paramNo, indexNo, xPos, yPos);
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.muteStatus.getMute(tags);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (value) {
                return this.mutePreset.loadPreset(tags);
            }
            else {
                return this.unmutePreset.loadPreset(tags);
            }
        });
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
}
class Channel {
    constructor(log, key, block, index1, index2) {
        this.log = log;
        this.key = key;
        this.block = block;
        this.index1 = index1;
        this.index2 = index2;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
    }
    getMute(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.block.getMute(this.index1, this.index2, tags);
        });
    }
    setMute(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.block.setMute(this.index1, this.index2, value, tags);
        });
    }
    getFader(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.block.getFader(this.index1, this.index2, tags);
        });
    }
    doesNotificationMatch(uniqueId, elmNo, paramNo, indexNo, xPos, yPos) {
        var _a;
        const match = this.block.getNotificationMatch(uniqueId, elmNo, paramNo, indexNo, xPos, yPos);
        if (debug) {
            this.log.debug(`doesNotificationMatch: after getNotification: matchType: ${match === null || match === void 0 ? void 0 : match.matchType}, isRouter: ${(_a = match === null || match === void 0 ? void 0 : match.command) === null || _a === void 0 ? void 0 : _a.isRouter}`);
        }
        if (match === undefined)
            return undefined;
        const { matchType, command } = match;
        const [thisXPos, thisYPos] = this.block.getPos(command, this.index1, this.index2);
        if (debug) {
            const logic = (thisXPos === undefined || thisXPos === xPos || (command === null || command === void 0 ? void 0 : command.isRouter)) &&
                (thisYPos === undefined || thisYPos === yPos);
            this.log.debug(`doesNotificationMatch: before return: logic=${logic}, thisXPos:${thisXPos}, thisYPos: ${thisYPos}, xPos: ${xPos}, yPos: ${yPos}`);
        }
        // Router NOTIFY responses will always have xPos as 0 which won't equal thisXPos.
        return (thisXPos === undefined || thisXPos === xPos || (command === null || command === void 0 ? void 0 : command.isRouter)) &&
            (thisYPos === undefined || thisYPos === yPos)
            ? { matchType: matchType, command }
            : undefined;
    }
    setFader(value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.block.setFader(this.index1, this.index2, value, tags);
        });
    }
    loadPreset(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.block.loadPreset(this.index1, this.index2, tags);
        });
    }
}
export class YamahaMtx {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.blocks = [];
        this.channels = [];
        this.modelName = 'mtx5d';
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const channels = [];
                for (const block of this.blocks) {
                    const detected = yield block.getDetected();
                    channels.push(...detected);
                }
                if (debug)
                    this.log.debug(`detected channels = ${JSON.stringify(channels, null, 4)}`);
                return channels;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`devinfo version`, /OK\s+devinfo\s+version\s+\"([\d\.]+)\"/i, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.findChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getMute(tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.findChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.setMute(value, tags);
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.findChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getFader(tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (debug)
                    this.log.debug(`driver: set volume: key: ${key}, volume: ${value}`);
                const channel = this.findChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.setFader(value, tags);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (debug) {
                    this.log.debug(`range: config = ${JSON.stringify(config)}, info = ${JSON.stringify(info)}  `);
                }
                const key = this.getKey(config);
                const channel = this.findChannel(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                const range = channel instanceof Channel
                    ? (_a = channel.block.volumeRanges) === null || _a === void 0 ? void 0 : _a[info.volumeType]
                    : undefined;
                return range !== null && range !== void 0 ? range : { max: 0, min: -5000, round: 1 };
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send(`devinfo serialno`, /OK\s+devinfo\s+serialno/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.channels.length = 0;
            // Determine the model name.
            const match = yield this.socket.send(`devinfo productname`, /OK devinfo productname "(\S+)"/i, ['configure']);
            if (match[1]) {
                const modelName = match[1].toLowerCase().replace('-', ''); // remove dash
                if (isModelName(modelName)) {
                    this.modelName = modelName;
                }
                else {
                    this.log.warning(`unknown model name: ${modelName}`);
                    this.modelName = 'mtx5d';
                }
                const modelNumber = yamahaMtxModelNumbers[this.modelName];
                if (modelNumber !== config.modelNumber) {
                    this.log.warning(`model number mismatch: expected ${config.modelNumber}, got ${modelNumber}`);
                }
            }
            yield this.getBlocks();
            if (this.notifyAsyncHandlerId) {
                this.socket.unregisterAsync(this.notifyAsyncHandlerId);
            }
            this.registerNotifyAsyncHandler();
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            if (debug) {
                this.log.debug(`Configured channels`);
                for (const chan of this.channels) {
                    this.log.debug(`chan.key: ${chan.key}`);
                }
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.notifyAsyncHandlerId) {
                this.socket.unregisterAsync(this.notifyAsyncHandlerId);
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send('devstatus runmode', /^OK\s+devstatus\s+runmode\s+(\S+)/i, ['initialize']);
            if (match[1].toLowerCase() !== '"normal"')
                return 'verifyFailed';
            yield this.socket.send('scpmode valuetype raw', // send raw (non-normalized) values
            /^OK\s+scpmode\s+valuetype/i, ['initialize']);
            return 'connected';
        });
    }
    // other class methods
    findChannel(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getBlocks() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.blocks.length = 0;
            const args = [this.channels, this.log, this.socket, this.state];
            for (const blockInfo of (_a = productInfo[this.modelName]) !== null && _a !== void 0 ? _a : []) {
                if (isChannelBlockInfo(blockInfo)) {
                    this.blocks.push(new ChannelBlock(blockInfo, ...args));
                }
                else if (isMatrixBlockInfo(blockInfo)) {
                    this.blocks.push(new MatrixBlock(blockInfo, ...args));
                }
            }
        });
    }
    getKey(config) {
        let channelConfig;
        let key = '';
        if (isYamahaMtxAudioChannelConfig(config)) {
            channelConfig = config;
            key = `.${channelConfig.channel}`;
        }
        else if (isYamahaMtxPresetMuteConfig(config)) {
            channelConfig = config.muteStatus;
            key = `preset.${channelConfig.channel}`;
        }
        else {
            throw new Error(`invalid config: ${JSON.stringify(config)}`);
        }
        if (channelConfig.index1 !== undefined)
            key += `.${channelConfig.index1}`;
        if (channelConfig.index2 !== undefined)
            key += `.${channelConfig.index2}`;
        return key;
    }
    queryStateAfterPresetLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const chan of this.channels) {
                if (debug)
                    this.log.debug(`queryStateAfterPreset: chan.key = ${chan.key}`);
                if (chan.isMuteConfigured) {
                    const chanState = yield chan.getMute(['muteSync']);
                    this.state.mutes.update(chan.key, chanState);
                }
                if (chan.isVolumeConfigured) {
                    const chanState = yield chan.getFader(['volumeSync']);
                    this.state.volumes.update(chan.key, chanState);
                }
            }
        });
    }
    // Update the mute / volume control points based on the NOTIFY async responses that are
    // generated by the MTX-MRX Editor software or other control systems.
    //
    // Use the sscurrent preset response to trigger requerying mutes and volumes instead of
    // ssrecall response because querying after the ssrecall response can cause an InternalError
    // response.
    registerNotifyAsyncHandler() {
        this.notifyAsyncHandlerId = this.socket.registerAsync(/NOTIFY\s+set\s+MTX:mem_512\/(\d+)\/(\d)\/(\d+)\/(\d+)\/(\d+)\/(\d+)\s+\d\s+\d\s+([+-]?\d+)|NOTIFY\s+sscurrent\s+(\d+)/i, (match) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (match && match[8] !== undefined) {
                // A new preset is current, so query the mute and volume state.
                yield this.queryStateAfterPresetLoaded();
            }
            else {
                // An individual channel update was received.
                const uniqueId = parseInt(match[1], 10);
                const elmNo = parseInt(match[2], 10);
                const xPos = parseInt(match[3], 10); // zero-based, single index channels
                const yPos = parseInt(match[4], 10); // output channel for matrix crosspoints
                const paramNo = parseInt(match[5], 10);
                const indexNo = parseInt(match[6], 10);
                const value = parseInt(match[7], 10);
                for (const chan of this.channels) {
                    if (debug)
                        this.log.debug(`async: before doesNotificationMatch: chan.key: ${chan.key}`);
                    const match = chan.doesNotificationMatch(uniqueId, elmNo, paramNo, indexNo, 
                    // xPos will always be 0 for routers, but that's okay because we only
                    // care about the yPos. We will handle router side effects below.
                    xPos, yPos);
                    if (match === undefined)
                        continue;
                    const { matchType, command } = match;
                    if (debug) {
                        this.log.debug(`    async: after doesNotificationMatch: chan.key: ${chan.key}, matchType = ${matchType}, isRouter: ${command === null || command === void 0 ? void 0 : command.isRouter}, value: ${value}`);
                    }
                    // Router blocks require handling of the mute side-effect since only one
                    // channel can be unmuted at a time, or all channels can be muted.
                    if (matchType === 'mute') {
                        if (command.isRouter) {
                            const mutedValue = value === 0;
                            if (mutedValue) {
                                chan.block.muteColumn((_a = chan.index2) !== null && _a !== void 0 ? _a : 0);
                                break;
                            }
                            else {
                                const index1 = value - 1;
                                if (debug) {
                                    this.log.debug(`chan: ${chan.key}, index1: ${chan.index1}, index2: ${chan.index2}, value: ${value}, chan.index1===value-1 : ${chan.index1 === index1}`);
                                }
                                // Mute the other configured input channels to this output.
                                chan.block.handleMuteSideEffects(index1, (_b = chan.index2) !== null && _b !== void 0 ? _b : 0, false);
                                // It's possible that a crosspoint gets unmuted, but we don't
                                // have a configured channel for it. So, we need check before
                                // unmuting.
                                if (chan.index1 === index1) {
                                    this.state.mutes.update(chan.key, false);
                                    // Don't need to loop through more channels because the
                                    // correct channel was unmuted and the other channels to
                                    // that output were muted.
                                    break;
                                }
                            }
                        }
                        else {
                            let mutedValue = value === 0;
                            if (debug) {
                                this.log.debug(`async update: mutedValue = ${mutedValue}`);
                            }
                            if (command.invertMutePolarity)
                                mutedValue = !mutedValue;
                            this.state.mutes.update(chan.key, mutedValue);
                        }
                    }
                    else if (matchType === 'volume') {
                        this.state.volumes.update(chan.key, value);
                    }
                }
            }
        }));
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isYamahaMtxPresetMuteConfig(config)) {
                const presetInfo = Object.assign(Object.assign({}, info), { filter: 'preset' });
                const mutePresetKey = this.getKey(config.mutePreset);
                const mutePresetResult = this.validateChannel(mutePresetKey, presetInfo);
                if (typeof mutePresetResult === 'string')
                    return mutePresetResult;
                const [mutePresetBlock, mutePresetIndex1, mutePresetIndex2] = mutePresetResult;
                const mutePresetChannel = new Channel(this.log, mutePresetKey, mutePresetBlock, mutePresetIndex1, mutePresetIndex2);
                const unmutePresetKey = this.getKey(config.unmutePreset);
                const unmutePresetResult = this.validateChannel(unmutePresetKey, presetInfo);
                if (typeof unmutePresetResult === 'string')
                    return unmutePresetResult;
                const [unmutePresetBlock, unmutePresetIndex1, unmutePresetIndex2] = unmutePresetResult;
                const unmutePresetChannel = new Channel(this.log, unmutePresetKey, unmutePresetBlock, unmutePresetIndex1, unmutePresetIndex2);
                const muteStatusKey = this.getKey(config.muteStatus);
                const muteStatusResult = this.validateChannel(muteStatusKey, info);
                if (typeof muteStatusResult === 'string')
                    return muteStatusResult;
                const [muteStatusBlock, muteStatusIndex1, muteStatusIndex2] = muteStatusResult;
                const muteStatusChannel = new Channel(this.log, muteStatusKey, muteStatusBlock, muteStatusIndex1, muteStatusIndex2);
                const key = this.getKey(config);
                let channel = this.findChannel(key);
                if (channel === undefined) {
                    channel = new PresetMuteChannel(this.getKey(config), mutePresetChannel, unmutePresetChannel, muteStatusChannel);
                    channel.isMuteConfigured = true;
                    this.channels.push(channel);
                }
                else {
                    return 'duplicate';
                }
            }
            else {
                const key = this.getKey(config);
                const result = this.validateChannel(key, info);
                if (typeof result === 'string')
                    return result;
                const [block, index1, index2] = result;
                let channel = this.findChannel(key);
                if (channel === undefined) {
                    channel = new Channel(this.log, key, block, index1, index2);
                    this.channels.push(channel);
                }
                else if ((info.type === 'mute' && channel.isMuteConfigured) ||
                    (info.type === 'volume' && channel.isVolumeConfigured)) {
                    return 'duplicate';
                }
                if (info.type === 'mute') {
                    channel.isMuteConfigured = true;
                }
                else if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                }
            }
            return 'ok';
        });
    }
    validateChannel(key, info) {
        const [, blockType, index1Str, index2Str] = key.split('.');
        const index1 = index1Str ? parseInt(index1Str, 10) : undefined;
        const index2 = index2Str ? parseInt(index2Str, 10) : undefined;
        const block = this.blocks.find(block => block.blockType === blockType);
        if (block === undefined)
            return 'missing';
        if (!block.indicesAreValid(index1, index2))
            return 'incompatible';
        if (!block.filters[info.filter])
            return 'incompatible';
        return [block, index1, index2];
    }
}
// static properties
YamahaMtx.channelsConfigName = 'yamahaMtxControlChannels';
YamahaMtx.driverType = 'socketDriver';
YamahaMtx.key = '-OQ9z3n5XlrJIpSxd4Y7';
YamahaMtx.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\n'],
    },
    sendTerminator: '\n',
};
YamahaMtx.socketOptions = [
    Object.assign(Object.assign({}, YamahaMtx.baseSocketOptions), { port: 49280 }),
    Object.assign(Object.assign({}, YamahaMtx.baseSocketOptions), { port: 4999 }),
];
